import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Create a new channel. Super admins must pick which tenant it belongs to. */
export default function NewChannelDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [resolution, setResolution] = useState("1080p");
  const [tenantId, setTenantId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setSlug(""); setSlugDirty(false); setResolution("1080p"); setTenantId("");
    if (isSuperAdmin) {
      setLoadingTenants(true);
      api.get("/tenants")
        .then((r) => setTenants(r.data || []))
        .catch(() => toast.error("Could not load tenants"))
        .finally(() => setLoadingTenants(false));
    }
  }, [open, isSuperAdmin]);

  const effectiveSlug = useMemo(() => (slugDirty ? slug : slugify(name)), [name, slug, slugDirty]);

  const create = async () => {
    if (!name.trim()) { toast.error("Channel name is required"); return; }
    if (isSuperAdmin && !tenantId) { toast.error("Select which tenant this channel belongs to"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), slug: effectiveSlug, resolution };
      if (isSuperAdmin) body.tenant_id = tenantId;
      const r = await api.post("/channels", body);
      toast.success(`Channel "${r.data.name}" created — set it to Live when ready`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create channel");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="new-channel-dialog">
        <DialogHeader className="relative pb-4 mb-2">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display">Create channel</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            New channels start Idle — no worker runs until you set them Live.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isSuperAdmin && (
            <div data-testid="new-channel-tenant-field">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tenant</Label>
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={loadingTenants}
                className="mt-1 w-full h-9 rounded-sm bg-background border border-border text-sm px-2"
                data-testid="new-channel-tenant-select"
              >
                <option value="">{loadingTenants ? "Loading tenants…" : "Select a tenant…"}</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Super admins own no single tenant, so pick the one this channel belongs to.</p>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Channel name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AFRO MUSIC HD"
              className="mt-1 rounded-sm font-display"
              data-testid="new-channel-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Slug</Label>
            <Input
              value={effectiveSlug}
              onChange={(e) => { setSlug(slugify(e.target.value)); setSlugDirty(true); }}
              placeholder="afro-music-hd"
              className="mt-1 rounded-sm font-mono text-sm"
              data-testid="new-channel-slug-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">Used in the public /watch/{effectiveSlug || "…"} link.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Resolution</Label>
            <div className="mt-1 flex rounded-sm border border-border overflow-hidden" data-testid="new-channel-resolution">
              {["720p", "1080p", "4K"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setResolution(r)}
                  data-testid={`new-channel-resolution-${r}`}
                  className={`flex-1 px-3 py-1.5 text-xs font-mono uppercase transition-colors ${resolution === r ? "bg-fuchsia-500/20 text-fuchsia-300" : "text-muted-foreground hover:bg-muted/40"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-border" data-testid="new-channel-cancel">Cancel</Button>
          <Button onClick={create} disabled={saving} className="rounded-sm tenant-btn" data-testid="new-channel-create">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create channel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
